<!--
  The top of the sidebar, above the pages. It shows only what the member can
  act on, so most members see nothing here and the list starts at Home.

  The name, the photo, the title, the place and the startups are gone: the
  member knows them, and the account block in the sidebar foot shows the name
  and the photo and opens the profile.

  A mentor has two profiles, the mentor and the mentee. A two-part switch
  shows both and marks the one that is on; one click changes it, the same as
  "Switch to mentee profile" in the account menu. A member with no mentor profile sees no
  control. The rank is on the mentor's Home page, not here.

  The state of the profile, which used to be a grey bar above every page,
  is a block of its own: the account is not active yet (with the share done
  and the next item), or the mentor profile is hidden. One state at a time,
  as a block that leads to the fix. With no state there is no block.

  In the icon rail the control goes (the account menu changes the profile)
  and the state is one icon with a dot.
-->
<template>
  <div
    v-if="canSwitchSide || status"
    class="gm-sb-profile"
    data-tour="profile-mode"
    :class="{ 'is-mentee': canSwitchSide && shownSide === 'mentee' }"
  >
    <div
      v-if="canSwitchSide"
      class="gm-sb-profile-switch"
      role="radiogroup"
      aria-label="Profile"
    >
      <span class="gm-sb-profile-switch-thumb" aria-hidden="true" />
      <button
        v-for="option in SIDES"
        :key="option.side"
        type="button"
        role="radio"
        class="gm-sb-profile-switch-option"
        :aria-checked="shownSide === option.side"
        :disabled="!!pendingSide"
        @click="switchSide(option.side)"
      >
        <span class="gm-sb-profile-mode-dot" aria-hidden="true" />
        {{ option.label }}
      </button>
    </div>

    <InertiaLink
      v-if="status"
      v-tooltip="{
        content: `${status.title}. ${status.action}`,
        placement: 'right',
        disabled: !isRail,
      }"
      :href="status.href"
      class="gm-sb-profile-status"
      :class="`is-${status.kind}`"
    >
      <UserRoundIcon class="gm-sb-profile-status-icon" aria-hidden="true" />
      <span class="gm-sb-profile-status-body">
        <span class="gm-sb-profile-status-row">
          <span class="gm-sb-profile-status-dot" aria-hidden="true" />
          <b>{{ status.title }}</b>
          <span
            v-if="status.percent !== null"
            class="gm-sb-profile-status-share"
          >
            {{ status.percent }}%
          </span>
        </span>
        <span v-if="status.percent !== null" class="gm-sb-profile-bar">
          <span :style="{ width: `${status.percent}%` }" />
        </span>
        <span v-if="status.text" class="gm-sb-profile-status-text">
          {{ status.text }}
        </span>
        <span class="gm-sb-upgrade-go gm-sb-profile-status-go">
          <span>{{ status.action }}</span>
          <ArrowRightIcon aria-hidden="true" />
        </span>
      </span>
    </InertiaLink>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { router } from '@inertiajs/vue3'
import ArrowRightIcon from '~icons/lucide/arrow-right'
import UserRoundIcon from '~icons/lucide/user-round'
import { useProfileMode } from './useProfileMode'
import { useShellNotices } from './useShellNotices'

defineProps<{ isRail: boolean }>()

type Side = 'mentor' | 'mentee'

// Mentee at the left, so the thumb moves right for Mentor.
const SIDES: { side: Side; label: string }[] = [
  { side: 'mentee', label: 'Mentee' },
  { side: 'mentor', label: 'Mentor' },
]

const { profileStatus: status } = useShellNotices()

// Only a mentor has the two profiles (User#toggle_current_actor).
const { hasTwoProfiles: canSwitchSide, isMenteeMode } = useProfileMode()
const side = computed<Side>(() => (isMenteeMode.value ? 'mentee' : 'mentor'))

// The label and the dot change at the click. The server changes the actor
// and sends the member back to the same page, which then shows the other
// profile.
const pendingSide = ref<Side | null>(null)
const shownSide = computed(() => pendingSide.value ?? side.value)

const switchSide = (next: Side) => {
  if (pendingSide.value || next === side.value) return
  pendingSide.value = next
  router.patch(
    '/switch-actor',
    {},
    {
      onFinish: () => {
        pendingSide.value = null
      },
    },
  )
}
</script>

<style>
/* Unscoped, like the rest of the sidebar: the icon rail rule keys off the
   sidebar's own width (the gm-sb container). Every class starts with gm-sb. */
.gm-sb-profile {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
/* The two profiles side by side, the full width of the column, with the
   round ends of Get matched under it. One click changes the profile. The
   white thumb sits under the profile that is on, and its dot has the colour
   of the profile (useProfileMode), the same as the line at the top of the
   page. */
.gm-sb-profile-switch {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 2.25rem;
  padding: 0.1875rem;
  border-radius: 999px;
  background: var(--color-neutral-100);
}
.gm-sb-profile-switch-thumb {
  position: absolute;
  top: 0.1875rem;
  bottom: 0.1875rem;
  left: 0.1875rem;
  width: calc(50% - 0.1875rem);
  border-radius: 999px;
  background: var(--color-white, #fff);
  box-shadow:
    0 1px 2px rgb(0 0 0 / 0.06),
    0 0 0 1px var(--color-neutral-200);
  transform: translateX(100%);
  transition: transform 0.2s ease;
}
.gm-sb-profile.is-mentee .gm-sb-profile-switch-thumb {
  transform: translateX(0);
}
.gm-sb-profile-switch-option {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4375rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-neutral-500);
  transition: color 0.15s ease;
}
.gm-sb-profile-switch-option:hover {
  color: var(--color-neutral-900);
}
.gm-sb-profile-switch-option[aria-checked='true'] {
  font-weight: 600;
  color: var(--color-neutral-900);
}
.gm-sb-profile-switch-option:disabled {
  cursor: default;
}
.gm-sb-profile-switch-option:focus-visible,
.gm-sb-profile-status:focus-visible {
  outline: 2px solid var(--color-primary-500);
  outline-offset: 2px;
}
/* Only the profile that is on shows its dot. */
.gm-sb-profile-mode-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--gm-mode-mentor);
  transition: background-color 0.2s ease;
}
.gm-sb-profile.is-mentee .gm-sb-profile-mode-dot {
  background: var(--gm-mode-mentee);
}
.gm-sb-profile-switch-option[aria-checked='false'] .gm-sb-profile-mode-dot {
  display: none;
}
.gm-sb-profile-bar {
  display: block;
  height: 0.25rem;
  border-radius: 999px;
  background: var(--color-primary-50);
  overflow: hidden;
}
.gm-sb-profile-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--color-secondary-500);
}
/* The state of the profile. The dot carries the colour of the state, so the
   block stays as calm as the sidebar. */
.gm-sb-profile-status {
  position: relative;
  display: block;
  min-width: 0;
  padding: 0.625rem 0.75rem;
  border-radius: 0.75rem;
  background: var(--color-neutral-50);
  box-shadow: inset 0 0 0 1px var(--color-neutral-100);
  font-size: 0.8125rem;
  line-height: 1.35;
  color: var(--color-neutral-600);
}
.gm-sb-profile-status-body {
  display: grid;
  gap: 0.375rem;
}
.gm-sb-profile-status-icon {
  display: none;
  width: 1.25rem;
  height: 1.25rem;
  color: var(--color-neutral-600);
}
.gm-sb-profile-status-row {
  display: flex;
  align-items: baseline;
  gap: 0.4375rem;
}
.gm-sb-profile-status-row b {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  color: var(--color-neutral-900);
}
.gm-sb-profile-status-share {
  font-size: 0.6875rem;
  color: var(--color-neutral-500);
  font-variant-numeric: tabular-nums;
}
.gm-sb-profile-status-dot {
  flex: none;
  align-self: center;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: var(--color-warning-800);
}
.gm-sb-profile-status.is-hidden .gm-sb-profile-status-dot {
  background: var(--color-error-500);
}
.gm-sb-profile-status-text {
  text-wrap: balance;
}
/* The link is .gm-sb-upgrade-go (AppSidebar), the same as on the foot cards.
   The block has no fill to change on hover, so the link shows the hover. */
.gm-sb-profile-status-go svg {
  transition: transform 120ms ease;
}
.gm-sb-profile-status:hover .gm-sb-profile-status-go span {
  text-decoration: underline;
  text-underline-offset: 2px;
}
.gm-sb-profile-status:hover .gm-sb-profile-status-go svg {
  transform: translateX(2px);
}
@media (prefers-reduced-motion: reduce) {
  .gm-sb-profile-switch-thumb,
  .gm-sb-profile-switch-option,
  .gm-sb-profile-mode-dot {
    transition: none;
  }
}

/* The icon rail: no row, and the state is one icon with its dot. A mentor
   with no state shows nothing here. */
@container gm-sb (max-width: 7.5rem) {
  .gm-sb-profile-switch {
    display: none;
  }
  .gm-sb-profile:not(:has(.gm-sb-profile-status)) {
    display: none;
  }
  .gm-sb-profile-status {
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    display: grid;
    place-items: center;
    border-radius: 0.625rem;
    /* No fill: only the row of the current page has one. */
    background: none;
    box-shadow: none;
  }
  .gm-sb-profile-status:hover .gm-sb-profile-status-icon {
    color: var(--color-neutral-900);
  }
  .gm-sb-profile-status-icon {
    display: block;
  }
  .gm-sb-profile-status-row {
    position: static;
  }
  .gm-sb-profile-status-row b,
  .gm-sb-profile-status-share,
  .gm-sb-profile-bar,
  .gm-sb-profile-status-text,
  .gm-sb-profile-status-go {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .gm-sb-profile-status-dot {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    box-shadow: 0 0 0 2px #fff;
  }
}
</style>
